<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;

    /**
     * Component is used to make some content draggable
     * @property {String} key - mandatory property has to be unique in a set of Droppables
     * @property {String} [draggableGroupKey] - draggableInstance key to attach Draggable to
     * @property {Boolean} [selected=false] - Draggable is selected
     * @property {Boolean} [disabled=false] - dragAndDrop operation is disabled
     * @property {Number} [delayTouch] - in ms; override default value of `dragAndDropAction`
     * @property {Number} [delayMouse] - in ms; override default value of `dragAndDropAction`
     * @property {Number} [distanceTouch] - in px; override default value  of `dragAndDropAction`
     * @property {Number} [distanceMouse] - in px; override default value  of `dragAndDropAction`
     * @fires dragStart {CustomEvent} custom event fired when Draggable has started being dragged
     * @fires dragStop {CustomEvent} custom event fired when Draggable has stopped being dragged
     * @fires click {CustomEvent} custom event fired when Draggable has been clicked
     */

    import { dragAndDropAction } from '@oat-sa-private/ui-core';
    import { createEventDispatcher } from 'svelte';

    export let key;
    export let draggableGroupKey = 'draganddrop';
    export let focusable = true; //tabindex=-1 if true, no tabindex if false
    export let disabled = false;
    export let delayTouch;
    export let delayMouse;
    export let distanceTouch;
    export let distanceMouse;

    const dispatch = createEventDispatcher();

    function handleDragStart(e) {
        dispatch('dragStart', e.detail);
        e.preventDefault();
    }

    function handleDragStop(e) {
        dispatch('dragStop', e.detail);
        e.preventDefault();
    }

    function handleRemove() {
        dispatch('remove', { key });
    }
</script>

<style>
    .draggable-container {
        width: 100%;
        height: 100%;
        cursor: pointer;
    }

    /* defined on body! class comes from drag-and-drop library */
    :global(body.draggable--is-dragging) {
        cursor: grabbing;
    }
    :global(body.draggable--is-dragging) .draggable-container {
        cursor: unset;
    }
</style>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-tabindex -->
<div
    class="draggable-container"
    tabindex={focusable ? '-1' : void 0}
    use:dragAndDropAction={{ draggableGroupKey, draggable: true, key, disabled, delayTouch, delayMouse, distanceTouch, distanceMouse }}
    on:dragStart={handleDragStart}
    on:dragStop={handleDragStop}
    on:click
    on:remove={handleRemove}>
    <slot />
</div>
